import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	OneToMany,
	OneToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { GenPartial, momentTransformer } from 'typeormgen';
import Character from '../characters/character.entity';
import EpisodicActor from '../episodic-actors/episodic-actor.entity';
import ProdBase from '../prod-base.entity';

@Entity('actors')
export default class Actor extends ProdBase {
	static readonly tableName = 'actors';

	@PrimaryGeneratedColumn()
	id: number;

	@Column('varchar')
	mobile_id: string;

	@Column('int')
	prod_id: number;

	@Column('int')
	episodic_actor_id: number;

	@Column('varchar')
	first_name: string;

	@Column('varchar')
	last_name: string;

	@Column('int')
	gender: number;

	@Column('varchar')
	age: string;

	@Column('varchar')
	email_primary: string;

	@Column('varchar')
	email_secondary: string;

	@Column('varchar')
	phone_primary: string;

	@Column('varchar')
	phone_secondary: string;

	@Column('varchar')
	height: string;

	@Column('varchar')
	weight: string;

	@Column({ type: 'date', transformer: momentTransformer })
	fitting_date: Moment;

	@Column({ type: 'time', transformer: momentTransformer })
	fitting_time: Moment;

	@Column('varchar')
	fitting_note: string;

	@Column('varchar')
	notes: string;

	@Column('varchar')
	allergies: string;

	@Column('varchar')
	blouse: string;

	@Column('varchar')
	bra: string;

	@Column('varchar')
	chest: string;

	@Column('varchar')
	dress: string;

	@Column('varchar')
	eyes: string;

	@Column('varchar')
	hair: string;

	@Column('varchar')
	hat: string;

	@Column('varchar')
	hip: string;

	@Column('varchar')
	inseam: string;

	@Column('varchar')
	pant: string;

	@Column('varchar')
	piercings: string;

	@Column('varchar')
	shirt: string;

	@Column('varchar')
	shoe: string;

	@Column('varchar')
	skirt: string;

	@Column('varchar')
	suit: string;

	@Column('varchar')
	tattoos: string;

	@Column('varchar')
	waist: string;

	@Column('varchar')
	glove: string;

	@Column('varchar')
	skin_tone: string;

	@Column('varchar')
	ring_size: string;

	@Column('int')
	ears_pierced: number;

	@Column('varchar')
	scars: string;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@OneToMany((type) => Character, (char) => char.actor)
	characters: Character[];

	@OneToOne((type) => EpisodicActor)
	@JoinColumn({
		name: 'episodic_actor_id',
	})
	episodicActor: EpisodicActor;

	char_ids: number[];

	static rules() {
		return {
			age: Joi.string().max(100).allow(null),
			allergies: Joi.string().max(1000),
			blouse: Joi.string().max(300),
			bra: Joi.string().max(300),
			char_ids: Joi.array().items(Joi.number().integer()).allow(null),
			chest: Joi.string().max(100),
			created_by: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			dress: Joi.string().max(300),
			ears_pierced: Joi.number().integer(),
			email_primary: Joi.string().max(100),
			email_secondary: Joi.string().max(100),
			episodic_actor_id: Joi.number().integer().allow(null),
			eyes: Joi.string().max(300),
			first_name: Joi.string().max(100),
			fitting_note: Joi.string().max(500),
			fitting_date: Joi.date().allow(null),
			fitting_time: Joi.date().allow(null),
			gender: Joi.number().integer().allow(null),
			glove: Joi.string().max(300).allow(null),
			hair: Joi.string().max(300).allow(null),
			hat: Joi.string().max(300),
			height: Joi.string().max(100),
			hip: Joi.string().max(300),
			id: Joi.number().integer(),
			inseam: Joi.string().max(300),
			last_name: Joi.string().max(100).allow(null, ''),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			mobile_id: Joi.string().max(36).allow(null, ''),
			notes: Joi.string().max(1000),
			pant: Joi.string().max(300),
			phone_primary: Joi.string().max(20),
			phone_secondary: Joi.string().max(20),
			piercings: Joi.string().max(1000),
			prod_id: Joi.number().integer(),
			ring_size: Joi.string().max(300),
			scars: Joi.string().max(300),
			shirt: Joi.string().max(300),
			shoe: Joi.string().max(300),
			skin_tone: Joi.string().max(300),
			skirt: Joi.string().max(300),
			suit: Joi.string().max(300),
			tattoos: Joi.string().max(1000),
			waist: Joi.string().max(300),
			weight: Joi.string().max(300),
		};
	}

	toJSON() {
		return {
			age: this.age,
			allergies: this.allergies,
			blouse: this.blouse,
			bra: this.bra,
			char_ids: this.char_ids ? this.char_ids : [],
			chest: this.chest,
			created_by: this.created_by,
			date_created: this.date_created,
			dress: this.dress,
			ears_pierced: this.ears_pierced,
			email_primary: this.email_primary,
			email_secondary: this.email_secondary,
			episodic_actor_id: this.episodic_actor_id,
			eyes: this.eyes,
			first_name: this.first_name,
			fitting_note: this.fitting_note,
			fitting_date: this.fitting_date,
			fitting_time: this.fitting_time,
			gender: this.gender,
			glove: this.glove,
			hair: this.hair,
			hat: this.hat,
			height: this.height,
			hip: this.hip,
			id: this.id,
			inseam: this.inseam,
			last_name: this.last_name,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			mobile_id: this.mobile_id,
			notes: this.notes,
			pant: this.pant,
			phone_primary: this.phone_primary,
			phone_secondary: this.phone_secondary,
			piercings: this.piercings,
			prod_id: this.prod_id,
			ring_size: this.ring_size,
			scars: this.scars,
			shirt: this.shirt,
			shoe: this.shoe,
			skin_tone: this.skin_tone,
			skirt: this.skirt,
			suit: this.suit,
			tattoos: this.tattoos,
			waist: this.waist,
			weight: this.weight,
		};
	}

	assign(props?: GenPartial<Actor>) {
		super.assign(props);

		if (props) {
			if (typeof props.fitting_date !== 'undefined') {
				this.fitting_date = momentTransformer.from(props.fitting_date);
			}
			if (typeof props.fitting_time !== 'undefined') {
				this.fitting_time = momentTransformer.from(props.fitting_time);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<Actor>) {
		super(props);
	}
}
